import type { TargetBehaviorFlags } from "api/types/charts";
import {
  atOrPhrase,
  directionsForTarget,
  formatThreshold,
  type MasteryBannerCriteria,
  type MasteryBannerEvaluation,
  type MasteryBannerPhase,
} from "./evaluateMasteryBanner";

// Consecutive-session default when the criterion omits a count, and the fixed
// "N of M" window used by the "approaching" banners (mirrors evaluateMasteryBanner).
const DEFAULT_CONSECUTIVE_POINTS = 3;
const ALMOST_WINDOW = 3;
const ALMOST_HITS_REQUIRED = 2;

/** Title-cases a phase id for the transition suffix, e.g. "maintenance" → "Maintenance". */
function capitalize(phase: string): string {
  return phase.charAt(0).toUpperCase() + phase.slice(1);
}

/**
 * " - target set to {Phase}" suffix for an auto-transition banner. The backend
 * applies the transition before raising the flag, so `phase` already holds the
 * destination. Empty when the phase is unknown (avoids a dangling "set to").
 */
function transitionSuffix(phase: MasteryBannerPhase | undefined): string {
  return phase ? ` - target set to ${capitalize(phase)}` : "";
}

/**
 * Maps the backend's `behavior_flags` to the target detail `MasteryBanner`,
 * building copy that mirrors the clinical alert design.
 *
 * The flags are the authoritative state signal (the backend evaluates them over
 * the recent session window); the criteria + phase supply the numbers and the
 * transition wording. At most one flag is expected true in practice — ties
 * resolve by clinical urgency:
 *
 *   auto_regressed → approaching_regression → set_to_maintenance → approaching_mastery
 *
 * ## Copy (per design)
 * - **Auto-Advanced** (green, two-line): "{N} consecutive sessions at criterion
 *   - target set to {Phase}" / "{N} sessions {at or above} {threshold}%".
 * - **Almost-Mastery** (blue, two-line): "Approaching mastery — 2 of 3
 *   consecutive sessions at criterion" / "2 of 3 sessions {at or above} {threshold}%".
 * - **Auto-Regressed** (red, single-line): "{N} consecutive sessions {at or below}
 *   {threshold}% - target set to {Phase}".
 * - **Almost-Regression** (amber, single-line): "Approaching regression — 2 of 3
 *   consecutive sessions {at or below} threshold".
 *
 * In `generalization` phase the mastery copy swaps "criterion" → "generalization
 * criterion". Direction phrases ("at or above"/"at or below") are resolved from
 * both L1 dimension and target type via {@link directionsForTarget}:
 * (latency and behavior-reduction each invert the base direction, so a latency +
 * behavior-reduction target's two inversions cancel out) — never an implicit
 * "more is better" assumption.
 *
 * @param params.flags - `behavior_flags` from the session-metrics response.
 * @param params.masteryCriteria - Normalised mastery criterion (threshold + consecutive).
 * @param params.regressionCriteria - Normalised regression criterion.
 * @param params.phase - The target's current (post-transition) clinical phase.
 * @param params.targetType - Target type, for the comparison direction.
 * @param params.l1Dimension - Target's L1 behavioral dimension, for the comparison direction.
 * @returns The banner to render, or `null` when no signal is active.
 */
export function deriveMasteryBannerFromFlags(params: {
  flags: TargetBehaviorFlags | null | undefined;
  masteryCriteria?: MasteryBannerCriteria | null;
  regressionCriteria?: MasteryBannerCriteria | null;
  phase?: MasteryBannerPhase;
  targetType?: string | null;
  l1Dimension?: string | null;
}): MasteryBannerEvaluation | null {
  const { flags, masteryCriteria, regressionCriteria, phase, targetType, l1Dimension } = params;
  if (!flags) return null;

  const { masteryDirection, regressionDirection } = directionsForTarget({
    l1Dimension,
    targetType,
  });
  const isGen = phase === "generalization";
  const masteryN = masteryCriteria?.consecutive_points ?? DEFAULT_CONSECUTIVE_POINTS;
  const regressionN = regressionCriteria?.consecutive_points ?? DEFAULT_CONSECUTIVE_POINTS;
  const masteryThreshold = masteryCriteria?.threshold;
  const masteryUnit = masteryCriteria?.threshold_unit;
  const regressionThreshold = regressionCriteria?.threshold;
  const regressionUnit = regressionCriteria?.threshold_unit;
  const criterionWord = isGen ? "generalization criterion" : "criterion";

  // 1. Auto-Regressed (red, single-line) — highest urgency.
  if (flags.auto_regressed) {
    const thresholdClause =
      typeof regressionThreshold === "number"
        ? ` ${atOrPhrase(regressionDirection)} ${formatThreshold(regressionThreshold, regressionUnit)}`
        : ` ${atOrPhrase(regressionDirection)} threshold`;
    return {
      level: "regression",
      detail: `${regressionN} consecutive sessions${thresholdClause}${transitionSuffix(phase)}`,
    };
  }

  // 2. Almost-Regression (amber, single-line).
  if (flags.approaching_regression) {
    return {
      level: "approachingRegression",
      detail: `Approaching regression — ${ALMOST_HITS_REQUIRED} of ${ALMOST_WINDOW} consecutive sessions ${atOrPhrase(
        regressionDirection
      )} threshold`,
    };
  }

  // 3. Auto-Advanced (green, two-line).
  if (flags.set_to_maintenance) {
    return {
      level: "allTargetsMet",
      title: `${masteryN} consecutive sessions at ${criterionWord}${transitionSuffix(phase)}`,
      detail:
        typeof masteryThreshold === "number"
          ? `${masteryN} sessions ${atOrPhrase(masteryDirection)} ${formatThreshold(masteryThreshold, masteryUnit)}`
          : undefined,
    };
  }

  // 4. Almost-Mastery (blue, two-line).
  if (flags.approaching_mastery) {
    return {
      level: "approachingMastery",
      title: isGen
        ? `Approaching generalization — ${ALMOST_HITS_REQUIRED} of ${ALMOST_WINDOW} consecutive sessions at generalization criterion`
        : `Approaching mastery — ${ALMOST_HITS_REQUIRED} of ${ALMOST_WINDOW} consecutive sessions at criterion`,
      detail:
        typeof masteryThreshold === "number"
          ? `${ALMOST_HITS_REQUIRED} of ${ALMOST_WINDOW} sessions ${atOrPhrase(masteryDirection)} ${formatThreshold(masteryThreshold, masteryUnit)}`
          : undefined,
    };
  }

  return null;
}
